/**
 * Colour arithmetic for contrast-safe accents: WCAG 2.2 contrast ratios, and moving a colour's
 * perceived lightness (OKLCH, after Björn Ottosson's OKLab) until it reaches a ratio while its
 * hue stays. Pure functions on `#rrggbb` strings; the browser and Node run them alike.
 */
const HEX = /^#([0-9a-f]{6})$/i;
/** `#rrggbb` (any case) to sRGB channels 0…1; anything else is a programming error. */
function parseHex(hex) {
    const match = HEX.exec(hex);
    if (!match?.[1])
        throw new TypeError(`not a #rrggbb colour: ${hex}`);
    const n = Number.parseInt(match[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
function toHex(rgb) {
    return `#${rgb
        .map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'))
        .join('')}`;
}
const toLinear = (v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const toGamma = (v) => (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055);
/** WCAG relative luminance of an sRGB colour. */
function luminance(rgb) {
    const [r, g, b] = rgb.map(toLinear);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.2 contrast ratio of two `#rrggbb` colours, 1…21. */
export function contrastRatio(a, b) {
    const [hi, lo] = [luminance(parseHex(a)), luminance(parseHex(b))].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
function toOklch(hex) {
    const [r, g, b] = parseHex(hex).map(toLinear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
    return { l: L, c: Math.hypot(A, B), h: Math.atan2(B, A) };
}
/** Linear sRGB of an OKLCH colour; channels outside 0…1 mean it lies outside sRGB. */
function linearRgb({ l: L, c, h }) {
    const A = c * Math.cos(h);
    const B = c * Math.sin(h);
    const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
    const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
    const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
    return [
        4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ];
}
const EPSILON = 1e-6;
const inGamut = (rgb) => rgb.every((v) => v >= -EPSILON && v <= 1 + EPSILON);
const STEPS = 24;
/** The colour at lightness `l`, as saturated as sRGB allows up to the wanted chroma. */
function atLightness(color, l) {
    let low = 0;
    let high = color.c;
    if (!inGamut(linearRgb({ ...color, l }))) {
        for (let i = 0; i < STEPS; i++) {
            const mid = (low + high) / 2;
            if (inGamut(linearRgb({ ...color, l, c: mid })))
                low = mid;
            else
                high = mid;
        }
        high = low;
    }
    return toHex(linearRgb({ ...color, l, c: high }).map(toGamma));
}
/** Every background reaches the ratio against the colour. */
const reaches = (hex, backgrounds, ratio) => backgrounds.every((bg) => contrastRatio(hex, bg) >= ratio);
/**
 * The colour, made darker (`towards: 'dark'`) or lighter until it reaches `ratio` against every
 * background - as little as needed, so a colour that already does stays exactly as it is.
 */
export function fitContrast(hex, backgrounds, ratio, towards) {
    const exact = hex.toLowerCase();
    if (reaches(exact, backgrounds, ratio))
        return exact;
    const color = toOklch(exact);
    // Black and white always reach the ratios used here against the light and dark backgrounds.
    let far = towards === 'dark' ? 0 : 1;
    let near = color.l;
    for (let i = 0; i < STEPS; i++) {
        const mid = (far + near) / 2;
        if (reaches(atLightness(color, mid), backgrounds, ratio))
            far = mid;
        else
            near = mid;
    }
    const fitted = atLightness(color, far);
    return reaches(fitted, backgrounds, ratio) ? fitted : towards === 'dark' ? '#000000' : '#ffffff';
}
/** The same colour at another lightness and chroma, its hue turned by `turn` radians. */
export function shade(hex, { l, c, turn = 0 }) {
    const color = toOklch(hex);
    return atLightness({ l: color.l, c: c ?? color.c, h: color.h + turn }, l ?? color.l);
}
/** OKLCH lightness 0…1 and chroma of a colour, e.g. to scale its chroma. */
export function lightnessAndChroma(hex) {
    const { l, c } = toOklch(hex);
    return { l, c };
}
//# sourceMappingURL=color-contrast.js.map